// ---------------------------------------------------------------------------
// Sample library — one curated demo world ("Load sample library"), built in the
// store's wire shapes (spec/48-library-data-architecture.md §3). Deliberately
// seeds NO property vocabulary — properties are the user's curation, not ours.
// Self-contained — imagery is real Martini R2 assets
// (frontend/e2e/fixtures/canvas-test-assets.json); ids are deterministic
// so re-imports are stable.
// ---------------------------------------------------------------------------

import type { LibAsset, LibCollection, Property } from '@/store/libraryStore'
import type { CommunityLibrarySnapshot } from '@/types/shared'

// Fixed epoch so the sample is deterministic (spec sample, not live data).
const EPOCH = 1_717_200_000_000 // 2024-06-01T00:00:00Z

// Real R2 assets (public, CORS-clean). One URL per image — no native-aspect
// variant, so assets omit srcLarge and getAssetUrl falls back to src.
const A = 'https://staging.martini.video/v0.2/assets'
const img = (id: string, ext: string) => `${A}/${id}/${id}.${ext}`

// Portrait-leaning frames read as people.
const P1 = img('0159aea1-9a5a-4945-a6a7-a23ad64e64c4', 'png') // 9:16
const P2 = img('0331c489-fcc1-4dc1-9b55-82e950df827f', 'jpg') // 9:16
const P3 = img('03f9983e-9401-4578-b900-4b26a5101fae', 'jpg') // 3:4
const P4 = img('002de470-64e2-459e-afb9-e34d9ea7df62', 'jpeg') // 4:3

/** Curated hero stills for the empty-state showcase — faces teach faster than
    schematics, so these are the community library's own heroes (public prod
    CDN): what the showcase promises is what loads. Center card (1) is the hero. */
const CDN = 'https://cdn.martini.film/v0.2/assets'
export const SAMPLE_PREVIEW: { name: string; url: string }[] = [
  // i18n-ignore -- illustrative sample (empty-state preview)
  { name: 'Elin Vik', url: `${CDN}/85237df1-23e9-4706-ac6c-868ed0e131c8/85237df1-23e9-4706-ac6c-868ed0e131c8.png` },
  // i18n-ignore -- illustrative sample (empty-state preview)
  { name: 'Ms. Rambutan', url: `${CDN}/c7f2b956-0455-4229-b636-1d19750a535f/c7f2b956-0455-4229-b636-1d19750a535f.png` },
  // i18n-ignore -- illustrative sample (empty-state preview)
  {
    name: 'Veggie Village',
    url: `${CDN}/6f570b4a-d99d-4fa8-9ccb-d1c22e67a2f5/6f570b4a-d99d-4fa8-9ccb-d1c22e67a2f5.png`,
  },
]
// Landscape/wide frames read as places and props.
const L1 = img('00864cdd-a8af-4e46-8b47-af0f538bfdd7', 'jpeg') // 21:9
const L2 = img('00fea29c-cc42-41e4-b0d7-b2935a73b87a', 'png') // 21:9
// 0079fc5b (16:9) unused: a text-post screenshot, not filmic media.
const L4 = img('008ba11f-4135-49b6-9a1f-3526240ba36b', 'png') // 16:9
// 00041888 (3:2) unused: celebrity portrait — wrong tone for samples.
// 002ac7d4 (3:2) unused: gray placeholder image in the fixture set.
const L7 = img('0176dc0e-e803-4615-a167-b3aab3fab701', 'jpg') // 4:3

type AssetSpec = { name: string; kind: LibAsset['kind']; url: string; text?: string }
type CollSpec = {
  id: string
  name: string
  slug: string
  type: string
  folder: string
  tags: string[]
  usedIn: number
  heroIndex: number
  assets: AssetSpec[]
}

// Placeholder examples — DEV/PREVIEW ONLY, never prod (gated below). The prod
// community library is served live from the published source workspace (see
// SPEC below); these just let the team test the library UI locally.
// Curated by eye against the fixture imagery: every People collection reads as
// ONE person; gray-placeholder / text-screenshot / celebrity fixtures unused.
const EXAMPLE_SPEC: CollSpec[] = [
  {
    id: 'smp-col-sarah',
    name: 'Sarah',
    slug: 'sarah',
    type: 'People',
    folder: 'Characters',
    tags: ['lead'],
    usedIn: 8,
    heroIndex: 0,
    assets: [
      { name: 'Evening laugh', kind: 'image', url: P2 },
      {
        name: 'Look',
        kind: 'text',
        url: '',
        text: 'Sarah: late 20s, shoulder-length dark hair, warm smile, dressed in soft evening light',
      },
    ],
  },
  {
    id: 'smp-col-fred',
    name: 'Fred',
    slug: 'fred',
    type: 'People',
    folder: 'Characters',
    tags: ['night-shift'],
    usedIn: 4,
    heroIndex: 0,
    assets: [
      { name: 'Hallway, coat', kind: 'image', url: L4 },
      { name: 'Rehearsal room', kind: 'image', url: P4 },
    ],
  },
  {
    id: 'smp-col-bob',
    name: 'Bob',
    slug: 'bob',
    type: 'People',
    folder: 'Characters',
    tags: [],
    usedIn: 2,
    heroIndex: 0,
    assets: [{ name: 'Suit-up', kind: 'image', url: P1 }],
  },
  {
    id: 'smp-col-reception',
    name: 'The Reception',
    slug: 'the-reception',
    type: 'Places',
    folder: 'Locations',
    tags: [],
    usedIn: 5,
    heroIndex: 0,
    assets: [
      { name: 'Vows, interior', kind: 'image', url: L7 },
      { name: 'House party', kind: 'image', url: P3 },
    ],
  },
  {
    id: 'smp-col-corridor',
    name: 'The Corridor',
    slug: 'the-corridor',
    type: 'Places',
    folder: 'Locations',
    tags: [],
    usedIn: 3,
    heroIndex: 0,
    assets: [{ name: 'Walk-through, night', kind: 'image', url: L2 }],
  },
  {
    id: 'smp-col-island',
    name: 'The Island',
    slug: 'the-island',
    type: 'Places',
    folder: 'Locations',
    tags: [],
    usedIn: 1,
    heroIndex: 0,
    assets: [{ name: 'Aerial, day', kind: 'image', url: L1 }],
  },
]

// Prod serves the community library LIVE from the published source workspace
// (backend GET /library/community/snapshot, org from COMMUNITY_LIBRARY_ORG_ID)
// — curated in-app, no deploy to update. The examples above are the dev and
// preview fallback only, used when that endpoint isn't configured.
const IS_PROD = process.env.NEXT_PUBLIC_VERCEL_ENV === 'production'
const SPEC: CollSpec[] = IS_PROD ? [] : EXAMPLE_SPEC

/** Whether the community library has anything to load — gates its UI. Prod
    availability is the NEXT_PUBLIC_COMMUNITY_LIBRARY flag (set alongside the
    backend's COMMUNITY_LIBRARY_ORG_ID); dev/preview always have the examples. */
export const SAMPLE_LIBRARY_AVAILABLE = SPEC.length > 0 || process.env.NEXT_PUBLIC_COMMUNITY_LIBRARY === '1'

/** What an import works from — either the live community snapshot
    (normalizeCommunityLibrary) or the bundled examples (buildSampleLibrary). */
export type CommunitySeed = {
  collections: LibCollection[]
  assets: LibAsset[]
  folders: string[]
  tagVocab: string[]
  properties: Property[]
}

/**
 * Import shapes from a live community snapshot. The recipient's copies are
 * sample rows (provenance) with usage reset and rev restarted — but
 * collection/asset ids and timestamps stay the source's, so shared
 * /library?c=<id> links resolve and recency order matches the source.
 */
export function normalizeCommunityLibrary(snap: CommunityLibrarySnapshot): CommunitySeed {
  return {
    collections: snap.collections.map(c => ({ ...c, sample: true, usedIn: 0, rev: 1 })),
    assets: snap.assets.map(a => ({ ...a, rev: 1 })),
    folders: snap.folders,
    tagVocab: snap.tagVocab,
    properties: snap.properties,
  }
}

export function buildSampleLibrary(): CommunitySeed {
  const collections: LibCollection[] = []
  const assets: LibAsset[] = []

  SPEC.forEach((c, ci) => {
    // Stagger timestamps so "recently updated" sort has a stable order.
    const ts = EPOCH - ci * 86_400_000
    collections.push({
      id: c.id,
      name: c.name,
      slug: c.slug,
      folder: c.folder,
      tags: c.tags,
      heroAssetId: `${c.id}-a${c.heroIndex}`,
      sample: true,
      usedIn: c.usedIn,
      rev: 1,
      createdAt: ts,
      updatedAt: ts,
    })
    c.assets.forEach((a, ai) => {
      assets.push({
        id: `${c.id}-a${ai}`,
        collectionId: c.id,
        kind: a.kind,
        src: a.url,
        ...(a.text !== undefined ? { textValue: a.text } : {}),
        name: a.name,
        attrs: {},
        rev: 1,
        createdAt: ts,
        updatedAt: ts,
      })
    })
  })

  return {
    collections,
    assets,
    // Derived from the active seed so prod (empty) ships no stray folders/tags,
    // and a filled REAL_SEED brings its own structure automatically.
    folders: [...new Set(SPEC.map(c => c.folder).filter(Boolean))],
    tagVocab: [...new Set(SPEC.flatMap(c => c.tags))],
    properties: [],
  }
}
